<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiStandardButton, UiInfoBox } from "@shopexperts/shared";
import { useExpertWalletTransactions } from "@/app/composables/wallet";
import ExpertWalletTransactionCard from "./ExpertWalletTransactionCard.vue";

const props = defineProps<{
  actingOrgId: number | null | undefined;
}>();

const actingOrgId = computed(() => props.actingOrgId);

const {
  transactions,
  isLoading,
  error,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  refetch,
} = useExpertWalletTransactions({ actingOrgId, perPage: 20 });

const isEmpty = computed(
  () => !isLoading.value && !error.value && transactions.value.length === 0,
);
</script>

<template>
  <section class="font-archivo flex flex-col gap-4">
    <div class="flex items-center justify-between gap-3">
      <UiText as="h2" variant="h4" text="Activity" class="font-semibold text-se-dark" />
      <UiText
        v-if="transactions.length"
        as="span"
        variant="override"
        :text="`${transactions.length} shown`"
        class="text-xs text-se-gray-500"
      />
    </div>

    <!-- Error -->
    <UiInfoBox v-if="error" variant="error" title="Couldn't load transactions">
      <div class="flex flex-col items-start gap-3">
        <UiText
          as="p"
          variant="override"
          text="Something went wrong while loading your wallet activity. Please try again."
          class="text-sm text-se-gray-700"
        />
        <UiStandardButton variant="outline" size="sm" label="Retry" @click="() => refetch()" />
      </div>
    </UiInfoBox>

    <!-- Loading skeleton -->
    <ul v-else-if="isLoading" class="flex flex-col">
      <li v-for="n in 4" :key="n" class="relative flex gap-3 sm:gap-4">
        <div class="flex flex-col items-center pt-3">
          <span class="h-8 w-8 shrink-0 animate-pulse rounded-full bg-se-gray-200 ring-4 ring-secondary" />
          <span v-if="n !== 4" class="mt-1.5 w-px flex-1 bg-se-gray-200" />
        </div>
        <div class="mb-3 flex-1">
          <div class="flex flex-col gap-3 rounded-xl border border-se-gray-200 bg-se-white p-4">
            <div class="flex items-center justify-between">
              <div class="h-4 w-32 animate-pulse rounded bg-se-gray-200" />
              <div class="h-5 w-20 animate-pulse rounded bg-se-gray-200" />
            </div>
            <div class="h-3 w-40 animate-pulse rounded bg-se-gray-200" />
          </div>
        </div>
      </li>
    </ul>

    <!-- Empty -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-se-gray-300 bg-se-gray-50 px-6 py-12 text-center"
    >
      <span class="flex h-12 w-12 items-center justify-center rounded-full bg-se-white text-se-gray-500 shadow-sm">
        <UiIcon name="wallet" class="h-6 w-6" />
      </span>
      <UiText as="p" variant="h5" text="No transactions yet" class="font-semibold text-se-dark" />
      <UiText
        as="p"
        variant="override"
        text="Your wallet activity will appear here once you start earning from completed work."
        class="max-w-sm text-sm text-se-gray-500"
      />
    </div>

    <!-- Timeline -->
    <template v-else>
      <ul class="flex flex-col">
        <ExpertWalletTransactionCard
          v-for="(transaction, index) in transactions"
          :key="transaction.id"
          :transaction="transaction"
          :is-last="index === transactions.length - 1"
        />
      </ul>

      <div v-if="hasNextPage" class="flex justify-center">
        <UiStandardButton
          variant="outline"
          size="sm"
          :label="isFetchingNextPage ? 'Loading…' : 'Load more'"
          :disabled="isFetchingNextPage"
          @click="() => fetchNextPage()"
        />
      </div>
      <UiText
        v-else
        as="p"
        variant="override"
        text="You've reached the end of your activity."
        class="text-center text-xs text-se-gray-400"
      />
    </template>
  </section>
</template>
